import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { McpServerStatus } from '../../utils/axios/api';
import { McpServerItemProps } from './mcp-server-item.interface';

const STATUS_COLOR: Record<
  McpServerStatus,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  [McpServerStatus.Running]: 'success',
  [McpServerStatus.Failed]: 'error',
  [McpServerStatus.Restarting]: 'warning',
  [McpServerStatus.Starting]: 'info',
  [McpServerStatus.Closing]: 'default',
};

export function McpServerItemComponent({
  server,
  onRestart,
}: McpServerItemProps) {
  const { t } = useTranslation();
  const { isRestarting, statusColor } = useMemo(
    () => ({
      isRestarting: server.status === McpServerStatus.Restarting,
      statusColor: STATUS_COLOR[server.status],
    }),
    [server.status],
  );
  const statusLabel = t(`admin.mcp.status.${server.status}`);
  const restartLabel = t('admin.mcp.restart');
  const owner = useMemo(
    () => server.email ?? t('admin.mcp.unknownOwner'),
    [server.email, t],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        px: 1.5,
        py: 1.25,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body1" noWrap>
          {owner}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {server.type}
          {server.error ? ` · ${server.error}` : ''}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={statusLabel}
        color={statusColor === 'default' ? undefined : statusColor}
        variant={statusColor === 'default' ? 'outlined' : 'filled'}
      />
      <Tooltip title={restartLabel}>
        <span>
          <IconButton
            aria-label={restartLabel}
            onClick={onRestart}
            disabled={isRestarting}
          >
            {isRestarting ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshOutlinedIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
